import React, { useEffect } from "react";
import { Form, Input, Button, Select, message } from "antd";
import { MailOutlined, PhoneOutlined, EnvironmentOutlined } from "@ant-design/icons";
import PageBox from "../../layout/PageBox";
import { MyColor } from "../../config/myStyle";
import { observer } from "mobx-react-lite";
import contactStore from "../../stores/contactStore";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const ContactUs = () => {
    const { t, i18n } = useTranslation();
    const [form] = Form.useForm();

    const enquiryOptions = [
        { value: "general_enquiry", label: t("contact_us.general_enquiry") },
        { value: "donation", label: t("contact_us.donation") },
        { value: "in_kind_donation", label: t("contact_us.in_kind_donation") },
        { value: "guest_admission", label: t("contact_us.guest_admission") },
        { value: "corporate_donation", label: t("contact_us.corporate_donation") },
        { value: "volunteer_opportunities", label: t("contact_us.volunteer_opportunities") }
    ];


    const offices = [
        {
            name: t("contact_us.office_name"),
            address1: t("contact_us.address1"),
            address2: t("contact_us.address2"),
            phone1: t("contact_us.phone1"),
            phone2: t("contact_us.phone2"),
            email: t("contact_us.email"),
        },
    ];

    const handleFinish = (values: any) => {
        contactStore.createContact(values)
    };

    useEffect(() => {
        if (contactStore.createContactSuccess) {
            message.success(t("contact_us.submitted_success"))
            form.resetFields();
            contactStore.resetCreateContact()
        }
    }, [contactStore.createContactSuccess, form, t]);

    return (
        <PageBox spinning={contactStore.createContactStatus.loading}>
            <div className="flex flex-col"  >
                {/* 地图 */}
                <div style={{ width: "100%", aspectRatio: "16/9", minHeight: 128 }}>
                    <iframe
                        title="Food Angel Map"
                        src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3680.771695148048!2d114.1594921153719!3d22.33214498531154!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x340400fd77b2b0b7%3A0x3cfdc2d44e9dbf0!2z5YyX5Lqs5rCR5bGx6auY6Z2S5a2Q!5e0!3m2!1szh-TW!2shk!4v1688888888888!5m2!1s${i18n.language}!2shk`}
                        width="100%"
                        height="100%"
                        style={{ border: 0, borderRadius: 0, minHeight: 128 }}
                        allowFullScreen={true}
                        loading="lazy"
                    />
                </div>

                {/* 地址/电话/邮箱 */}
                <div className="bg-[white] mx-[15px] mt-[25px]">
                    {offices.map((office, idx) => (
                        <div key={idx} className="flex-col" style={{
                            borderRadius: 8,
                            padding: 5,
                            margin: "0 0 15px 0",
                            marginLeft: "auto",
                            marginRight: "auto",
                            fontSize: 14,
                        }}>
                            <div style={{ fontWeight: 500, fontSize: 17, marginBottom: 8 }}>{office.name}</div>
                            <div style={{ display: "flex", alignItems: 'center', marginBottom: 6 }}>
                                <EnvironmentOutlined style={{ color: MyColor.blue, fontSize: 16, marginRight: 6, marginTop: 2 }} />
                                <span>{office.address1}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: 'center', marginBottom: 6 }}>
                                <EnvironmentOutlined style={{ color: MyColor.blue, fontSize: 16, marginRight: 6, marginTop: 2 }} />
                                <span>{office.address2}</span>
                            </div>
                            <div style={{ display: "flex", alignItems: 'center', marginBottom: 6 }}>
                                <PhoneOutlined style={{ color: MyColor.blue, fontSize: 16, marginRight: 6, marginTop: 2 }} />
                                <a href={`tel:${office.phone1}`} style={{ color: MyColor.blue, marginRight: 12, textDecoration: "underline" }}>{office.phone1}</a>
                            </div>
                            <div style={{ display: "flex", alignItems: 'center', marginBottom: 6 }}>
                                <PhoneOutlined style={{ color: MyColor.blue, fontSize: 16, marginRight: 6, marginTop: 2 }} />
                                <a href={`tel:${office.phone2}`} style={{ color: MyColor.blue, textDecoration: "underline" }}>{office.phone2}</a>
                            </div>
                            <div style={{ display: "flex", alignItems: 'center', marginBottom: 6 }}>
                                <MailOutlined style={{ color: MyColor.blue, fontSize: 16, marginRight: 6, marginTop: 2 }} />
                                <a href={`mailto:${office.email}`} style={{ color: MyColor.blue, textDecoration: "underline" }}>{office.email}</a>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="h-[1px] bg-[rgba(0,0,0,0.4)] my-[20px] " />

                {/* 表单 */}
                <div style={{
                    background: "#faf7ed",
                    borderRadius: 20,
                }}>
                    <div className="text-[28px] text-center "
                        style={{ color: MyColor.blue, fontWeight: 600 }}>
                        {t("contact_us.contact_us")}
                    </div>

                    <Form
                        className="flex-col px-[18px] space-y-[10px] mt-[20px]"
                        form={form}
                        layout="vertical"
                        onFinish={handleFinish}
                        requiredMark={false}
                    >

                        <Form.Item
                            className="p-[0px] m-[0px]"
                            name="firstName"
                            rules={[{ required: true, message: t("contact_us.please_enter_first_name") }]}
                        >
                            <Input
                                className="h-[35px]" placeholder={t("contact_us.first_name")} />
                        </Form.Item>
                        <Form.Item
                            className="p-[0px] m-[0px]"
                            name="lastName"
                            rules={[{ required: true, message: t("contact_us.please_enter_last_name") }]}
                        >
                            <Input
                                className="h-[35px]" placeholder={t("contact_us.last_name")} />
                        </Form.Item>
                        <Form.Item
                            className="p-[0px] m-[0px]"
                            name="email"
                            rules={[
                                { required: true, message: t("contact_us.please_enter_email") },
                                { type: "email", message: t("contact_us.please_enter_valid_email") }
                            ]}
                        >
                            <Input
                                className="h-[35px]" placeholder={t("contact_us.email_label")} />
                        </Form.Item>
                        <Form.Item
                            className="p-[0px] m-[0px]"
                            name="mobile"
                            rules={[{ required: true, message: t("contact_us.please_enter_mobile") }]}
                        >
                            <Input
                                className="h-[35px]" placeholder={t("contact_us.mobile")} />
                        </Form.Item>
                        <Form.Item
                            className="p-[0px] m-[0px]"
                            name="type"
                            initialValue={enquiryOptions[0].value}
                            rules={[{ required: true, message: t("contact_us.please_select_enquiry_type") }]}
                        >
                            <Select className="h-[35px]">
                                {enquiryOptions.map(opt =>
                                    <Option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </Option>
                                )}
                            </Select>
                        </Form.Item>
                        <Form.Item
                            className="p-[0px] m-[0px]"
                            name="message"
                            rules={[{ required: true, message: t("contact_us.please_enter_message") }]}
                        >
                            <Input.TextArea placeholder={t("contact_us.enquiry_message")} maxLength={200} rows={4} />
                        </Form.Item>
                        <Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                style={{
                                    height: 48,
                                    width: 160,
                                    borderRadius: 48/2,
                                    background: "#bfa373",
                                    color: "white",
                                    fontWeight: 500,
                                    fontSize: 22,
                                    display: "block",
                                    margin: "0 auto",
                                    marginTop: 20,
                                    marginBottom: 40,
                                }}
                            >
                                {t("contact_us.submit")}
                            </Button>
                        </Form.Item>
                    </Form>
                </div>
            </div>
        </PageBox>
    );
};

export default observer(ContactUs);
